import React from 'react';
import { Box, Typography, Avatar, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PiSteeringWheelFill } from 'react-icons/pi';
import { BiCamera } from 'react-icons/bi';
import RoleChip from './RoleChip';
import { getCleansedPhoneNumber } from '@services/formatService';

export interface UserCardProps {
  avatarUrl?: string | null;
  displayName: string;
  email: string;
  phone?: string | null;
  role?: string;
  isActive?: boolean;
  isDriver?: boolean;
  driverStatus?: string;
  compact?: boolean;
  /** Larger avatar for hero/detail pages */
  hero?: boolean;
  onAvatarClick?: () => void;
}

const UserCard: React.FC<UserCardProps> = ({
  avatarUrl,
  displayName,
  email,
  phone,
  role,
  isActive = true,
  isDriver = false,
  driverStatus,
  compact = false,
  hero = false,
  onAvatarClick,
}) => {
  const theme = useTheme();
  const size = hero ? 64 : compact ? 34 : 40;
  const nameSize = hero ? 18 : compact ? 13 : 14;
  const emailSize = hero ? 14 : compact ? 12 : 13;
  const hasAvatar = !!avatarUrl;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1.5}
      sx={!isActive ? { opacity: 0.55 } : undefined}
    >
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          flexShrink: 0,
          ...(onAvatarClick && {
            cursor: 'pointer',
            '&:hover .avatar-overlay': { opacity: 1 },
          }),
        }}
        onClick={onAvatarClick}
      >
        <Avatar
          src={avatarUrl || undefined}
          alt={displayName}
          sx={{ width: size, height: size, fontSize: size * 0.4 }}
        >
          {displayName?.[0]?.toUpperCase()}
        </Avatar>
        {onAvatarClick && (
          <Box
            className="avatar-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.45)',
              opacity: hasAvatar ? 0 : hero ? 1 : 0.6,
              transition: 'opacity 100ms ease-in-out',
            }}
          >
            <BiCamera size={size * 0.4} color="#fff" />
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography
            sx={{ fontSize: nameSize, fontWeight: 500, lineHeight: 1.3 }}
            noWrap
          >
            {displayName || 'Unnamed User'}
          </Typography>
          {role && <RoleChip role={role} />}
          {isDriver && (
            <Tooltip title={`Driver${driverStatus ? ` (${driverStatus})` : ''}`}>
              <Box display="flex" alignItems="center" sx={{ color: theme.palette.secondary.main }}>
                <PiSteeringWheelFill size={compact ? 14 : 16} />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Typography
          sx={{ fontSize: emailSize, color: 'text.secondary', lineHeight: 1.3 }}
          noWrap
        >
          {email}
        </Typography>
        {hero && phone && (
          <Typography
            sx={{ fontSize: emailSize, color: 'text.secondary', lineHeight: 1.3 }}
            noWrap
          >
            {getCleansedPhoneNumber(phone) || phone}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default UserCard;
